import axios from 'axios';
import _ from 'lodash';
import {
  mapCookieToHeaders,
  mapResponseToJson,
  mapResponseToText,
  serializeCookie,
  UnsucessfulFetchError
} from '@atlassiansox/asc-core';
import { fetch } from 'cross-fetch';
import Cookies from 'js-cookie';

import {
  ActivateProductUpstreamResponse,
  ActivateProductResponse,
  ActivateProductRequest,
  AvailableProductsOptions,
  AvailableProductsResponse,
  AvailableProductsType,
  BitbucketWorkspaceOptions,
  BitbucketWorkspacesResponse,
  CheckAuthenticationMethodsAvailableResponse,
  CheckAuthenticationMethodsAvailableRequest,
  CheckCloudNameAvailabilityRequest,
  CheckCloudNameAvailabilityResponse,
  GetUserCloudSitesRequest,
  GetUserCloudSitesResponse,
  GetLicenseInformationResponse,
  GetLicenceInformationRequest,
  GetUserPermissionsIsPermittedRequest,
  GetUserPermissionsIsPermittedResponse,
  MeOptions,
  mapMeResponseToUserProfile,
  PostCloudSignUpRequest,
  ProductActivationStatusRequest,
  ProductActivationStatusResponse,
  PatchUserProfileRequest,
  RenameCloudRequest,
  RenameCloudResponse,
  SitesType,
  UserSignupRequest,
  UserSignupResponse,
  ProductActivateRequest,
  ProductActivateResponse,
  COFSProductActivateRequest,
  COFSProductActivateResponse,
  GetProductHubInfoResponse,
  GetCcpHamsStatus,
  GetCcpHamsResponse,
  GetAvailableSitesRequest,
  GetAvailableSitesResponse,
  ActivateDirectBuyProductRequest,
  ActivateDirectBuyProductResponse,
  GetCloudProductInfoRequest,
  CloudProductInfo,
  GetProductEditionRequest,
  GetProductEditionResponse,
  GetActivationAuthorizationRequest,
  GetActivationAuthorizationResponse,
  GetFeatureFlagRequest,
  GetFeatureFlagResponse,
  AvailableProductsLegacyResponse,
  getConsentConfigResponse,
  postConsentConfigResponse,
  GetFeatureFlagSyncRequest,
  GetFeatureFlagSyncResponse,
  PostExpandFreeValidationResponse,
  PostExpandFreeValidationRequest,
  GetRecommendedSiteNameResponse,
  GetRecommendedSiteNameRequest,
  GetAccountOrgsResponse,
  GetAccountOrgsRequest,
  CreateOrgRequest,
  CreateOrgResponse,
  CofsActivateProductExistingRequest,
  ActivateProductCofsResponse,
  BitbucketValidateWorkspaceSlugRequest,
  BitbucketValidateWorkspaceSlugResponse
} from './stargate-client.types';
import { PRODUCT_KEY_MAP } from './constants';
import { UserProfile } from './models';
import { getAtlCohortIdFromCookie, isValidSite } from './utils';
import { getReCaptchaKey } from './utils';

export interface IStargateClient {
  activateProduct(
    request: ActivateProductRequest
  ): Promise<ActivateProductResponse>;
  availableProducts(
    options: AvailableProductsOptions
  ): Promise<AvailableProductsResponse>;
  availableProductsLegacy(
    options: AvailableProductsOptions
  ): Promise<AvailableProductsLegacyResponse>;
  bitbucketWorkspaces(
    options: BitbucketWorkspaceOptions
  ): Promise<BitbucketWorkspacesResponse>;
  checkCloudNameAvailability(
    request: CheckCloudNameAvailabilityRequest
  ): Promise<CheckCloudNameAvailabilityResponse>;
  checkAuthenticationMethodsAvailable(
    request: CheckAuthenticationMethodsAvailableRequest
  ): Promise<CheckAuthenticationMethodsAvailableResponse>;
  getLicenceInformationStatus(
    request: GetLicenceInformationRequest
  ): Promise<GetLicenseInformationResponse>;
  getSignupServerXsrfToken(): Promise<string>;
  getProductActivationStatus(
    requires: ProductActivationStatusRequest
  ): Promise<ProductActivationStatusResponse>;
  getUserCloudSites(
    request: GetUserCloudSitesRequest
  ): Promise<GetUserCloudSitesResponse>;
  getUserPermissionsIsPermitted(
    request: GetUserPermissionsIsPermittedRequest
  ): Promise<GetUserPermissionsIsPermittedResponse>;
  getCurrentAuthenticatedUser(): Promise<unknown>;
  getProductHubInfo(): Promise<GetProductHubInfoResponse>;
  me(options: MeOptions): Promise<UserProfile>;
  patchUserProfile(request: PatchUserProfileRequest): Promise<unknown>;
  postCloudSignUp(payload: PostCloudSignUpRequest): Promise<unknown>;
  postProductActivate(
    request: ProductActivateRequest
  ): Promise<ProductActivateResponse>;
  postCOFSProductActivate(
    request: COFSProductActivateRequest
  ): Promise<COFSProductActivateResponse>;
  renameCloud(request: RenameCloudRequest): Promise<RenameCloudResponse>;
  userSignup(request: UserSignupRequest): Promise<UserSignupResponse>;
  getCcpHamsStatus(request: GetCcpHamsStatus): Promise<GetCcpHamsResponse>;
  getAvailableSites(
    request: GetAvailableSitesRequest
  ): Promise<GetAvailableSitesResponse>;
  activateDirectBuyProduct(
    request: ActivateDirectBuyProductRequest
  ): Promise<ActivateDirectBuyProductResponse>;
  getCloudProductInfo(
    request: GetCloudProductInfoRequest
  ): Promise<CloudProductInfo>;
  getProductEdition(
    request: GetProductEditionRequest
  ): Promise<GetProductEditionResponse>;
  getActivationAuthorization(
    request: GetActivationAuthorizationRequest
  ): Promise<GetActivationAuthorizationResponse>;
  getFeatureFlag(
    request: GetFeatureFlagRequest
  ): Promise<GetFeatureFlagResponse>;
  postExpandFreeValidation(
    request: PostExpandFreeValidationRequest
  ): Promise<PostExpandFreeValidationResponse>;
  getFeatureFlagSync(
    request: GetFeatureFlagSyncRequest
  ): Promise<GetFeatureFlagSyncResponse>;
  postConsentConfig(): Promise<postConsentConfigResponse>;
  getConsentConfig(): Promise<getConsentConfigResponse>;
  getRecommendedSiteName(
    request: GetRecommendedSiteNameRequest
  ): Promise<GetRecommendedSiteNameResponse>;
  getAccountOrgs(
    request: GetAccountOrgsRequest
  ): Promise<GetAccountOrgsResponse>;
  cofsCreateOrg(request: CreateOrgRequest): Promise<CreateOrgResponse>;
  cofsActivateProductExisting(
    request: CofsActivateProductExistingRequest
  ): Promise<ActivateProductCofsResponse>;
  bitbucketValidateWorkspaceSlug(
    request: BitbucketValidateWorkspaceSlugRequest
  ): Promise<BitbucketValidateWorkspaceSlugResponse>;
}

export const DEFAULT_STARGATE_BASE_URL =
  'https://wac.stg.internal.atlassian.com/gateway/api';
export const DEFAULT_IDENTITY_BASE_URL = 'https://id.staging.atl-paas.net';

export type StargateClientOptions = {
  readonly baseUrl: string;
  readonly disableRequestDeduplication?: boolean;
};

const openRequestCache = new Map<string, Promise<Response>>();

/**
 * @see https://bitbucket.org/atlassian/stargate/src/master/src/main/resources/application.yml
 */
export class StargateClient implements IStargateClient {
  public readonly baseUrl: string;
  public readonly disableRequestDeduplication?: boolean;

  public constructor({
    baseUrl,
    disableRequestDeduplication
  }: StargateClientOptions) {
    this.baseUrl = baseUrl;
    this.disableRequestDeduplication = disableRequestDeduplication;
  }

  /**
   * Deduplicate fetch requests made while there is an open request with the same cacheKey
   * @param cacheKey
   * @param input
   * @param init
   * @private
   */
  private async dedupeFetch(
    cacheKey: string,
    input: RequestInfo,
    init?: RequestInit
  ): Promise<Response> {
    if (this.disableRequestDeduplication) {
      return fetch(input, init);
    }

    // Check for an open request
    const cachedResult = openRequestCache.get(cacheKey);
    if (cachedResult) {
      return cachedResult.then((res) => res.clone());
    }

    // Create a new request based on the input
    const request = fetch(input, init);
    openRequestCache.set(cacheKey, request);

    // Return the request and clear it from cache when it completes
    return request.finally(() => {
      openRequestCache.delete(cacheKey);
    });
  }

  /**
   * Get the xsrf token required to make calls to the signup server
   *
   * @see https://bxp-signup-server.staging.atl-paas.net/swagger/#/Authentication/get_auth_xsrf
   */
  public async getSignupServerXsrfToken(webPlatform?: string): Promise<string> {
    return mapResponseToText(
      await fetch(`${this.baseUrl}/bxp/signup/auth/xsrf`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'accept': 'text/plain',
          'web-platform': webPlatform || 'no-platform'
        }
      })
    );
  }

  /**
   * Get the authentication methods available to a given user.
   *
   * @see https://bxp-signup-server.us-west-2.prod.atl-paas.net/swagger/#/Identity/post_authentication_methods_available
   */
  public async checkAuthenticationMethodsAvailable({
    email,
    application = 'wac',
    successRedirect,
    webPlatform
  }: CheckAuthenticationMethodsAvailableRequest): Promise<CheckAuthenticationMethodsAvailableResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const atlCohortId = getAtlCohortIdFromCookie();

    return mapResponseToJson(
      await fetch(
        `${this.baseUrl}/bxp/signup/authentication/methods-available`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xsrf-token': xsrfToken,
            'X-Atl-Cohort-Id': atlCohortId,
            'web-platform': webPlatform || 'no-platform'
          },
          credentials: 'include',
          body: JSON.stringify({
            email,
            application,
            continue: successRedirect
          })
        }
      )
    );
  }

  /**
   * Get whether a user has a permission for a particular resource.
   *
   * @see https://id-public-api-facade.prod.atl-paas.net/swagger.json
   */
  public async getUserPermissionsIsPermitted({
    permissionId,
    resourceId,
    dontRequirePrincipalInSite,
    webPlatform
  }: GetUserPermissionsIsPermittedRequest): Promise<GetUserPermissionsIsPermittedResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/permissions/permitted`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'web-platform': webPlatform || 'no-platform'
        },
        body: JSON.stringify({
          permissionId,
          resourceId,
          dontRequirePrincipalInSite
        })
      })
    );
  }

  /**
   * Returns details about the current authenticated user.
   * This is expected to be consistent with Atlassian account.
   *
   * @see https://id-public-api-facade.prod.atl-paas.net/swagger.json
   */
  public async getCurrentAuthenticatedUser(): Promise<unknown> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/me`, {
        credentials: 'include'
      })
    );
  }

  /**
   * Gets trial information for Atlassian products. Currently only supports
   *   JSW.
   *
   * @returns
   */
  public async getProductHubInfo(): Promise<GetProductHubInfoResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/producthub/info`, {
        credentials: 'include'
      })
    );
  }

  public async postCloudSignUp({
    payload,
    webPlatform
  }: PostCloudSignUpRequest): Promise<unknown> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/signup`, {
        method: 'POST',
        headers: {
          'web-platform': webPlatform || 'no-platform',
          'Content-Type': 'application/json',
          'atl-cookies': JSON.stringify({
            _ga: Cookies.get('_ga') || '',
            seg_xid: localStorage.getItem('seg_xid') || '',
            __atl_path: Cookies.get('__atl_path') || '',
            ajs_anonymous_id: Cookies.get('ajs_anonymous_id') || ''
          })
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      })
    );
  }

  /**
   * Rename a Cloud
   * @see https://cofs.dev.public.atl-paas.net/api.html#external__cloudId__rename_put
   */
  public async renameCloud({
    cloudId,
    cloudName,
    webPlatform
  }: RenameCloudRequest): Promise<RenameCloudResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cofs/${cloudId}/rename`, {
        method: 'put',
        body: JSON.stringify({
          cloudName
        }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'web-platform': webPlatform || 'no-platform'
        },
        credentials: 'include'
      })
    );
  }

  /**
   * @see https://cloud-name-availability.prod.atl-paas.net/swagger-ui/index.html#/name-availability-stargate-controller/checkAvailabilityUsingPOST
   */
  public async checkCloudNameAvailability({
    cloudName,
    webPlatform
  }: CheckCloudNameAvailabilityRequest): Promise<CheckCloudNameAvailabilityResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/hostname/cloud/availability/check`, {
        method: 'post',
        body: JSON.stringify({
          cloudName
        }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'web-platform': webPlatform || 'no-platform'
        },
        credentials: 'include'
      })
    );
  }

  public async getUserCloudSites({
    product,
    editions,
    adminAccess,
    licenseLevels,
    billingFrequency
  }: GetUserCloudSitesRequest): Promise<GetUserCloudSitesResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/users/cloudsites`, {
        method: 'post',
        body: JSON.stringify({
          product,
          editions,
          adminAccess,
          licenseLevels,
          billingFrequency
        }),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })
    );
  }

  /**
   * Get the new ais/available-products
   */
  public async availableProducts({
    includeInvalidSites,
    includeInvalidBitbucketSite,
    removeNonAdminSites
  }: AvailableProductsOptions): Promise<AvailableProductsResponse> {
    const reqUrl = `${this.baseUrl}/ais/available-products`;
    const response = await this.dedupeFetch(reqUrl, reqUrl, {
      method: 'GET',
      credentials: 'include'
    });
    if (response.ok) {
      const result = await response.json();
      let sites = (result && result.sites) || [];

      if (removeNonAdminSites) {
        sites = sites.filter((site: SitesType) => site.adminAccess);
      }

      if (!includeInvalidSites) {
        sites = sites.filter((site: SitesType) =>
          isValidSite({
            site,
            includeInvalidBitbucketSite
          })
        );
      }

      return Promise.resolve({
        sites,
        isNewUser: !!(result && result.isNewUser)
      });
    } else {
      throw new UnsucessfulFetchError({
        response
      });
    }
  }

  public async userSignup(
    request: UserSignupRequest
  ): Promise<UserSignupResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const atlCohortId = getAtlCohortIdFromCookie();

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/bxp/signup/identity/user-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xsrf-token': xsrfToken,
          'X-Atl-Cohort-Id': atlCohortId,
          'web-platform': request.webPlatform || 'no-platform',
          'recaptcha-site-key': getReCaptchaKey()
        },
        credentials: 'include',
        body: JSON.stringify({ ...request })
      })
    );
  }

  public async activateProduct(
    request: ActivateProductRequest
  ): Promise<ActivateProductResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const atlCohortId = getAtlCohortIdFromCookie();

    const upstreamResponse =
      await mapResponseToJson<ActivateProductUpstreamResponse>(
        await fetch(`${this.baseUrl}/bxp/signup/activate-product`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xsrf-token': xsrfToken,
            'X-Atl-Cohort-Id': atlCohortId,
            'web-platform': request.webPlatform || 'no-platform',
            'x-b3-sampled': '1',
            'recaptcha-site-key': getReCaptchaKey()
          },
          credentials: 'include',
          body: JSON.stringify(request)
        })
      );

    return upstreamResponse;
  }

  public async getProductActivationStatus(
    request: ProductActivationStatusRequest
  ): Promise<ProductActivationStatusResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();

    return mapResponseToJson(
      await fetch(
        `${this.baseUrl}/bxp/signup/activate/status/${request.provisioningRequestId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'xsrf-token': xsrfToken,
            'web-platform': request.webPlatform || 'no-platform'
          },
          credentials: 'include'
        }
      )
    );
  }

  public async getLicenceInformationStatus(
    request: GetLicenceInformationRequest
  ): Promise<GetLicenseInformationResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();

    return mapResponseToJson(
      await fetch(
        `${this.baseUrl}/bxp/signup/cloud/${request.cloudId}/license-information`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'xsrf-token': xsrfToken,
            'web-platform': request.webPlatform || 'no-platform'
          },
          credentials: 'include'
        }
      )
    );
  }
  // for attaching a product to an existing site, xflow worked on the endpoint
  public async postProductActivate(
    request: ProductActivateRequest
  ): Promise<ProductActivateResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/bxp/signup/product/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xsrf-token': xsrfToken,
          'web-platform': request.webPlatform || 'no-platform',
          'recaptcha-site-key': getReCaptchaKey()
        },
        credentials: 'include',
        body: JSON.stringify(request)
      })
    );
  }
  // for attaching a product to an existing site using COFS, xflow worked on the endpoint
  public async postCOFSProductActivate(
    request: COFSProductActivateRequest
  ): Promise<COFSProductActivateResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const { cloudId } = request;
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/xflow/${cloudId}/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xsrf-token': xsrfToken,
          'web-platform': request.webPlatform || 'no-platform'
        },
        credentials: 'include',
        body: JSON.stringify(request)
      })
    );
  }
  /**
   * Indentity calls
   */
  public async me({
    cloudSessionTokenCookie
  }: MeOptions): Promise<UserProfile> {
    const url = `${this.baseUrl}/me`;
    const response = await this.dedupeFetch(
      `${url}-${cloudSessionTokenCookie}`,
      url,
      {
        headers: mapCookieToHeaders(cloudSessionTokenCookie),
        credentials: 'include'
      }
    );
    if (response.ok) {
      return mapMeResponseToUserProfile(await response.json());
    } else {
      throw new UnsucessfulFetchError({
        response
      });
    }
  }

  /**
   * Get a list of Bitbucket workspaces
   * @param nextUrl - override the request URL for pagination
   * bitbucket/user/permissions/workspaces?sort=workspace.slug
   */
  public async bitbucketWorkspaces({
    nextUrl,
    cloudSessionTokenCookie,
    urlQuery
  }: BitbucketWorkspaceOptions): Promise<BitbucketWorkspacesResponse> {
    let reqUrl = `${this.baseUrl}/bitbucket/user/permissions/workspaces`;
    if (nextUrl) {
      reqUrl = nextUrl;
    }
    if (urlQuery) {
      reqUrl = `${reqUrl}?${urlQuery}`;
    }

    const response = await fetch(reqUrl, {
      headers: mapCookieToHeaders(cloudSessionTokenCookie),
      credentials: 'include'
    });

    if (response.ok) {
      return Promise.resolve(await response.json());
    } else {
      throw new UnsucessfulFetchError({
        response
      });
    }
  }

  /**
   * The legacy get availableProducts call from IdentityClient
   */
  public async availableProductsLegacy({
    cloudSessionTokenCookie,
    includeInvalidSites,
    includeInvalidBitbucketSite
  }: AvailableProductsOptions): Promise<AvailableProductsLegacyResponse> {
    const reqUrl = `${this.baseUrl}/available-products/api/available-products`;
    const response = await fetch(reqUrl, {
      headers: mapCookieToHeaders(cloudSessionTokenCookie),
      credentials: 'include'
    });

    if (response.ok) {
      const result = await response.json();
      let sites = (result && result.sites) || [];

      sites = _.remove(
        _.map(sites, (site: AvailableProductsType) => {
          const products = _.map(site.availableProducts, (product) => {
            return PRODUCT_KEY_MAP[product.productType];
          }).filter((product) => product !== undefined);

          if (!_.isEmpty(products)) {
            return {
              adminAccess: site.adminAccess,
              cloudId: site.cloudId,
              displayName: site.displayName,
              url: site.url,
              products
            };
          } else {
            return null;
          }
        }),
        (site) => site !== undefined && site !== null
      );

      if (!includeInvalidSites) {
        sites = sites.filter((site: SitesType) =>
          isValidSite({
            site,
            includeInvalidBitbucketSite
          })
        );
      }

      return Promise.resolve(sites);
    } else {
      throw new UnsucessfulFetchError({
        response
      });
    }
  }

  public async patchUserProfile(
    request: PatchUserProfileRequest
  ): Promise<unknown> {
    const { body, userId, cloudSessionTokenCookie } = request;

    const options = {
      headers: {
        'content-type': 'application/json',
        'Cookie': serializeCookie(cloudSessionTokenCookie)
      }
    };
    return new Promise((resolve, reject) => {
      axios
        .patch(`${this.baseUrl}/users/${userId}/manage/profile`, body, options)
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err.toString());
        });
    });
  }

  public async getCcpHamsStatus(
    request: GetCcpHamsStatus
  ): Promise<GetCcpHamsResponse> {
    const { cloudId, offeringKeys } = request;

    return mapResponseToJson(
      await fetch(
        `${this.baseUrl}/bag/rollout/service?cloudId=${cloudId}&offeringKeys=${offeringKeys}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        }
      )
    );
  }

  public async getAvailableSites(
    request: GetAvailableSitesRequest
  ): Promise<GetAvailableSitesResponse> {
    const { orderId, transactionAccount } = request;

    return mapResponseToJson(
      await fetch(
        `${this.baseUrl}/bxp/signup/v1/order/${orderId}/available-sites`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Transaction-Account': transactionAccount,
            'web-platform': request.webPlatform || 'no-platform'
          },
          credentials: 'include'
        }
      )
    );
  }

  public async activateDirectBuyProduct(
    request: ActivateDirectBuyProductRequest
  ): Promise<ActivateDirectBuyProductResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();

    return mapResponseToJson(
      await fetch(
        `${this.baseUrl}/bxp/signup/v1/order/${request.orderId}/activation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xsrf-token': xsrfToken,
            'X-Transaction-Account': request.transactionAccountId,
            'web-platform': request.webPlatform || 'no-platform'
          },
          credentials: 'include',
          body: JSON.stringify({
            timezone: request.timezone,
            region: request.region,
            site: request.site
          })
        }
      )
    );
  }

  public async getCloudProductInfo(
    request: GetCloudProductInfoRequest
  ): Promise<CloudProductInfo> {
    return mapResponseToJson<CloudProductInfo>(
      await fetch(
        `${this.baseUrl}/bxp/signup/v1/cloud/${request.cloudId}/product-information`,
        {
          headers: {
            'web-platform': request.webPlatform || 'no-platform'
          },
          credentials: 'include'
        }
      )
    );
  }

  public async getProductEdition(
    request: GetProductEditionRequest
  ): Promise<GetProductEditionResponse> {
    const { cloudId, productKey } = request;

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/edition/${cloudId}/${productKey}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })
    );
  }

  public async getConsentConfig(): Promise<getConsentConfigResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/preferencesV2/consent-config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })
    );
  }

  public async postConsentConfig(): Promise<postConsentConfigResponse> {
    return mapResponseToJson(
      await fetch(`${this.baseUrl}/preferencesV2/consent-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      })
    );
  }

  public async postExpandFreeValidation(
    request: PostExpandFreeValidationRequest
  ): Promise<PostExpandFreeValidationResponse> {
    const { cloudId, productKeys } = request;

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/cloud/expand-free-validation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          cloudId,
          productKeys
        })
      })
    );
  }

  public async getActivationAuthorization(
    request: GetActivationAuthorizationRequest
  ): Promise<GetActivationAuthorizationResponse> {
    const { productKeys, productEdition, cloudId, siteName } = request;
    const atlCohortId = getAtlCohortIdFromCookie();

    return mapResponseToJson(
      await fetch(
        `${
          this.baseUrl
        }/bxp/signup/activation-authorization?${new URLSearchParams({
          productKeys: productKeys.join(','),
          productEdition: productEdition,
          cloudId: cloudId ?? '',
          siteName
        })}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Atl-Cohort-Id': atlCohortId,
            'web-platform': request.webPlatform || 'no-platform'
          },
          credentials: 'include'
        }
      )
    );
  }

  public async getFeatureFlag(
    request: GetFeatureFlagRequest,
    webPlatform?: string
  ): Promise<GetFeatureFlagResponse> {
    const flag = request;

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/bxp/signup/feature-flag/${flag}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'web-platform': webPlatform || 'no-platform'
        },
        credentials: 'include'
      })
    );
  }

  public async getFeatureFlagSync(
    request: GetFeatureFlagSyncRequest,
    webPlatform?: string
  ): Promise<GetFeatureFlagSyncResponse> {
    const flag = request;
    const atlCohortId = getAtlCohortIdFromCookie();

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/bxp/signup/feature-flag/sync/${flag}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Atl-Cohort-Id': atlCohortId,
          'web-platform': webPlatform || 'no-platform'
        },
        credentials: 'include'
      })
    );
  }

  public async getRecommendedSiteName(
    request: GetRecommendedSiteNameRequest
  ): Promise<GetRecommendedSiteNameResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const atlCohortId = getAtlCohortIdFromCookie();
    const headers = mapCookieToHeaders(request.cloudSessionTokenCookie);
    Object.assign(headers, {
      'Content-Type': 'application/json',
      'xsrf-token': xsrfToken,
      'X-Atl-Cohort-Id': atlCohortId,
      'web-platform': request.webPlatform || 'no-platform'
    });

    const response = await mapResponseToJson<GetRecommendedSiteNameResponse>(
      await fetch(`${this.baseUrl}/bxp/signup/site/recommended-name`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(request)
      })
    );

    return response;
  }

  public async getAccountOrgs({
    includeVortexMode,
    webPlatform
  }: GetAccountOrgsRequest): Promise<GetAccountOrgsResponse> {
    const params = includeVortexMode === true ? `?includeVortexMode=true` : '';

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/bxp/signup/account/orgs${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'web-platform': webPlatform || 'no-platform'
        },
        credentials: 'include'
      })
    );
  }

  public async cofsCreateOrg(
    request: CreateOrgRequest
  ): Promise<CreateOrgResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const atlCohortId = getAtlCohortIdFromCookie();

    return mapResponseToJson(
      await fetch(`${this.baseUrl}/bxp/signup/cofs-create-organization`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'xsrf-token': xsrfToken,
          'X-Atl-Cohort-Id': atlCohortId,
          'web-platform': request.webPlatform || 'no-platform'
        },
        credentials: 'include',
        body: JSON.stringify(request)
      })
    );
  }

  public async cofsActivateProductExisting(
    request: CofsActivateProductExistingRequest
  ): Promise<ActivateProductCofsResponse> {
    const xsrfToken = await this.getSignupServerXsrfToken();
    const atlCohortId = getAtlCohortIdFromCookie();

    const upstreamResponse =
      await mapResponseToJson<ActivateProductCofsResponse>(
        await fetch(
          `${this.baseUrl}/bxp/signup/cofs-activate-product-existing`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'xsrf-token': xsrfToken,
              'X-Atl-Cohort-Id': atlCohortId,
              'web-platform': request.webPlatform || 'no-platform'
            },
            credentials: 'include',
            body: JSON.stringify(request)
          }
        )
      );

    return upstreamResponse;
  }

  public async bitbucketValidateWorkspaceSlug({
    workspaceSlug
  }: BitbucketValidateWorkspaceSlugRequest): Promise<BitbucketValidateWorkspaceSlugResponse> {
    return mapResponseToJson<BitbucketValidateWorkspaceSlugResponse>(
      await fetch(
        `${this.baseUrl}/bitbucket/api/internal/workspace/slug/validate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            workspace_slug: workspaceSlug
          })
        }
      )
    );
  }
}
